<template>
  <div class="csrc-fields-wrap">
    <!-- source type picker -->
    <div class="csrc-picker mb-3">
      <label class="form-label small text-muted d-block mb-2">Source type</label>
      <div class="csrc-tiles">
        <button
          v-for="t in sourceTypes" :key="t.value" type="button"
          class="csrc-tile"
          :class="{ 'csrc-tile--on': form.type === t.value, 'csrc-tile--locked': lockFor(t.value).locked }"
          :disabled="lockFor(t.value).locked"
          :title="lockFor(t.value).locked ? `Available on the ${lockFor(t.value).plan} plan` : ''"
          @click="selectType(t.value)"
        >
          <span class="csrc-tile__icon">
            <CygnusIcon name="lock" v-if="lockFor(t.value).locked" />
            <CygnusIcon v-else :name="t.icon" />
          </span>
          <span class="csrc-tile__name">{{ t.label }}</span>
          <span v-if="lockFor(t.value).locked" class="csrc-tile__lock">Available on {{ lockFor(t.value).plan || 'a higher plan' }}</span>
          <span v-else class="csrc-tile__desc">{{ t.desc }}</span>
        </button>
      </div>
    </div>

    <div class="mb-2">
      <label class="form-label small text-muted">Name</label>
      <input v-model="form.name" class="form-control form-control-sm" placeholder="HN: marketing automation">
    </div>

    <div class="mb-2" style="max-width:260px">
      <label class="form-label small text-muted">Schedule</label>
      <select v-model="form.schedule" class="form-select form-select-sm">
        <option v-for="s in scheduleOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
      </select>
      <p v-if="form.schedule !== 'manual'" class="form-text small text-muted mb-0">
        Automatic re-runs re-fetch from the provider — Places sweeps re-bill each run.
      </p>
    </div>

    <div class="csrc-fields">
      <!-- hackernews -->
      <template v-if="form.type === 'hackernews'">
        <div class="row g-2">
          <div class="col-md-6">
            <label class="form-label small text-muted">Query <span class="text-danger">*</span></label>
            <input v-model="form.query" class="form-control form-control-sm" placeholder="marketing automation">
          </div>
          <div class="col-md-3">
            <label class="form-label small text-muted">Tags</label>
            <select v-model="form.tags" class="form-select form-select-sm">
              <option value="story">story</option>
              <option value="comment">comment</option>
              <option value="poll">poll</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label small text-muted">Hits per page</label>
            <input v-model.number="form.hitsPerPage" type="number" min="1" class="form-control form-control-sm">
          </div>
        </div>
      </template>

      <!-- reddit -->
      <template v-else-if="form.type === 'reddit'">
        <div class="row g-2">
          <div class="col-md-5">
            <label class="form-label small text-muted">Query <span class="text-danger">*</span></label>
            <input v-model="form.query" class="form-control form-control-sm" placeholder="marketing automation">
          </div>
          <div class="col-md-4">
            <label class="form-label small text-muted">Subreddit</label>
            <input v-model="form.subreddit" class="form-control form-control-sm" placeholder="e.g. webdev (blank = global)">
          </div>
          <div class="col-md-3">
            <label class="form-label small text-muted">Limit</label>
            <input v-model.number="form.limit" type="number" min="1" class="form-control form-control-sm">
          </div>
        </div>
        <div class="mt-2">
          <label class="form-label small text-muted">User agent <small>— advanced, optional</small></label>
          <input v-model="form.user_agent" class="form-control form-control-sm" placeholder="e.g. musubi-crawler/1.0">
        </div>
      </template>

      <!-- linkedin -->
      <template v-else-if="form.type === 'linkedin'">
        <p class="form-text small mb-2"><CygnusIcon name="info-circle" class="me-1" />Runs on the browser worker tier.</p>
        <div class="row g-2">
          <div class="col-md-6">
            <label class="form-label small text-muted">Query <span class="text-danger">*</span></label>
            <input v-model="form.query" class="form-control form-control-sm" placeholder="need help with marketing automation">
          </div>
          <div class="col-md-3">
            <label class="form-label small text-muted">Limit</label>
            <input v-model.number="form.limit" type="number" min="1" class="form-control form-control-sm">
          </div>
          <div class="col-md-3 d-flex align-items-end">
            <div class="form-check form-switch mb-1">
              <input v-model="form.auto_enrich" class="form-check-input" type="checkbox" role="switch" id="pw-li-enrich">
              <label class="form-check-label small" for="pw-li-enrich">Auto-enrich</label>
            </div>
          </div>
        </div>
      </template>

      <!-- site_crawl -->
      <template v-else-if="form.type === 'site_crawl'">
        <div class="mb-2">
          <label class="form-label small text-muted">Seed URLs <span class="text-danger">*</span> <small class="text-muted">— one per line</small></label>
          <textarea v-model="form.seed_urls" rows="4" class="form-control form-control-sm font-monospace" placeholder="https://acme-agency.co.uk&#10;https://another-firm.com"></textarea>
        </div>
        <div class="row g-2">
          <div class="col-md-3">
            <label class="form-label small text-muted">Max</label>
            <input v-model.number="form.max" type="number" min="1" class="form-control form-control-sm">
          </div>
          <div class="col-md-4 d-flex align-items-end">
            <div class="form-check form-switch mb-1">
              <input v-model="form.auto_enrich" class="form-check-input" type="checkbox" role="switch" id="pw-sc-enrich">
              <label class="form-check-label small" for="pw-sc-enrich">Auto-enrich</label>
            </div>
          </div>
        </div>
      </template>

      <!-- local_search -->
      <template v-else-if="form.type === 'local_search'">
        <div class="row g-2 align-items-end">
          <div class="col-md-8" v-if="!form.useCoords">
            <label class="form-label small text-muted">Location <span class="text-danger">*</span></label>
            <input v-model="form.location" class="form-control form-control-sm" placeholder="Manchester, UK">
          </div>
          <div class="col-12" v-if="form.useCoords">
            <div class="row g-2">
              <div class="col-md-4">
                <label class="form-label small text-muted">Latitude</label>
                <input v-model.number="form.lat" type="number" step="any" class="form-control form-control-sm">
              </div>
              <div class="col-md-4">
                <label class="form-label small text-muted">Longitude</label>
                <input v-model.number="form.lon" type="number" step="any" class="form-control form-control-sm">
              </div>
            </div>
          </div>
          <div class="col-12">
            <button type="button" class="btn btn-link btn-sm p-0" @click="form.useCoords = !form.useCoords">
              {{ form.useCoords ? '← Use a place name instead' : 'Use coordinates instead' }}
            </button>
          </div>
        </div>

        <div class="csrc-field mt-2">
          <label class="form-label small text-muted">Search radius</label>
          <div class="csrc-slider">
            <input type="range" min="1" max="50" step="1" v-model.number="form.radius_miles" class="form-range">
            <span class="csrc-slider__val">{{ form.radius_miles }} mi</span>
          </div>
        </div>

        <!-- Vertical picker — presets compile to OSM key=value selectors -->
        <div class="csrc-field mt-3">
          <label class="form-label small text-muted">
            What kind of business? <small>- pick one or more, or leave empty to search every business type</small>
          </label>
          <div v-if="btLoading" class="text-muted small py-1">Loading business types…</div>
          <template v-else>
            <input v-model="btSearch" class="form-control form-control-sm mb-2" placeholder="Search business types - e.g. agency, cafe, plumber">
            <div class="csrc-chips csrc-btlist">
              <button
                v-for="p in filteredBusinessTypes" :key="p.slug" type="button"
                class="csrc-chip" :class="{ 'csrc-chip--on': form.business_types.includes(p.slug) }"
                :title="p.coverage === 'places' ? 'Rarely mapped on OpenStreetMap' : ''"
                @click="toggleBusinessType(p.slug)"
              >{{ p.label }}<span v-if="p.coverage === 'places'" class="csrc-chip__flag">Places</span></button>
            </div>
            <p v-if="!filteredBusinessTypes.length" class="form-text small text-muted mb-0">
              No preset matches “{{ btSearch }}”. Use Google Places with that as a free-text query, or add raw OSM tags under Advanced below.
            </p>
            <p v-if="coverageHint" class="csrc-hint small mt-2 mb-0">
              <CygnusIcon name="info-circle" class="me-1" />{{ coverageHint }}
            </p>
          </template>
        </div>

        <details class="csrc-adv mt-2">
          <summary>Advanced targeting - broad OSM keys &amp; raw tags</summary>
          <div class="csrc-adv__body">
            <label class="form-label small text-muted">
              Broad categories <small>- used only while no business type is picked above</small>
            </label>
            <div class="csrc-chips">
              <button
                v-for="c in categoryOptions" :key="c" type="button"
                class="csrc-chip" :class="{ 'csrc-chip--on': form.categories.includes(c), 'csrc-chip--muted': form.business_types.length }"
                @click="toggleCategory(c)"
              >{{ c }}</button>
            </div>
            <label class="form-label small text-muted mt-2">
              Raw OSM selectors <small>- one per line, e.g. <code>office=advertising_agency</code> or a bare key</small>
            </label>
            <textarea v-model="form.categories_raw" rows="3" class="form-control form-control-sm font-monospace" placeholder="office=advertising_agency&#10;shop=bakery"></textarea>
            <p class="form-text small text-muted mb-0">Anything that isn’t a valid key or key=value pair is dropped (and logged) at crawl time.</p>
          </div>
        </details>

        <div class="row g-2 mt-1">
          <div class="col-md-3">
            <label class="form-label small text-muted">Max results</label>
            <input v-model.number="form.max" type="number" min="1" class="form-control form-control-sm">
          </div>
          <div class="col-md-4 d-flex align-items-end">
            <div class="form-check form-switch mb-1">
              <input v-model="form.include_no_website" class="form-check-input" type="checkbox" role="switch" id="pw-ls-nowebsite">
              <label class="form-check-label small" for="pw-ls-nowebsite">Include no-website firms</label>
            </div>
          </div>
          <div class="col-md-3 d-flex align-items-end">
            <div class="form-check form-switch mb-1">
              <input v-model="form.auto_enrich" class="form-check-input" type="checkbox" role="switch" id="pw-ls-enrich">
              <label class="form-check-label small" for="pw-ls-enrich">Auto-enrich</label>
            </div>
          </div>
        </div>

        <div class="mt-2">
          <label class="form-label small text-muted">Overpass URL <small>— advanced, optional</small></label>
          <input v-model="form.overpass_url" class="form-control form-control-sm" placeholder="https://overpass-api.de/api/interpreter">
        </div>
      </template>

      <!-- google_places -->
      <template v-else-if="form.type === 'google_places'">
        <div class="row g-2 align-items-end">
          <div class="col-md-4">
            <label class="form-label small text-muted">Query</label>
            <input v-model="form.query" class="form-control form-control-sm" placeholder="businesses">
          </div>
          <div class="col-md-8" v-if="!form.useCoords">
            <label class="form-label small text-muted">Location <span class="text-danger">*</span></label>
            <input v-model="form.location" class="form-control form-control-sm" placeholder="Hebden Bridge, UK">
          </div>
          <div class="col-12" v-if="form.useCoords">
            <div class="row g-2">
              <div class="col-md-4">
                <label class="form-label small text-muted">Latitude</label>
                <input v-model.number="form.lat" type="number" step="any" class="form-control form-control-sm">
              </div>
              <div class="col-md-4">
                <label class="form-label small text-muted">Longitude</label>
                <input v-model.number="form.lon" type="number" step="any" class="form-control form-control-sm">
              </div>
            </div>
          </div>
          <div class="col-12">
            <button type="button" class="btn btn-link btn-sm p-0" @click="form.useCoords = !form.useCoords">
              {{ form.useCoords ? '← Use a place name instead' : 'Use coordinates instead' }}
            </button>
          </div>
        </div>

        <div class="csrc-field mt-2">
          <label class="form-label small text-muted">Search radius</label>
          <div class="csrc-slider">
            <input type="range" min="1" max="31" step="1" v-model.number="form.radius_miles" class="form-range">
            <span class="csrc-slider__val">{{ form.radius_miles }} mi</span>
          </div>
        </div>

        <div class="row g-2 mt-1">
          <div class="col-md-3">
            <label class="form-label small text-muted">Max results <small>(cap 60)</small></label>
            <input v-model.number="form.max" type="number" min="1" max="60" class="form-control form-control-sm" @change="clampGooglePlacesMax">
          </div>
          <div class="col-md-4 d-flex align-items-end">
            <div class="form-check form-switch mb-1">
              <input v-model="form.auto_enrich" class="form-check-input" type="checkbox" role="switch" id="pw-gp-enrich">
              <label class="form-check-label small" for="pw-gp-enrich">Auto-enrich</label>
            </div>
          </div>
        </div>
        <p class="form-text small text-muted mb-0">Billed per request — needs a Places API key in Settings ▸ Enrichment. Dedupes against OSM by website.</p>
      </template>

      <!-- geo_osm (CN-1 — Canal Nav waterway sweep) -->
      <template v-else-if="form.type === 'geo_osm'">
        <p class="form-text small mb-2">
          <CygnusIcon name="droplet" class="me-1" />Sweeps a per-category radius around Canal Nav's waterway seed
          (anchors on edge/bridge/facility points — never town centroids). Resumable across runs; free OSM/Overpass.
        </p>
        <div class="row g-2">
          <div class="col-md-4">
            <label class="form-label small text-muted">Default radius (m)</label>
            <input v-model.number="form.radius_default_m" type="number" min="10" step="10" class="form-control form-control-sm">
          </div>
          <div class="col-md-4">
            <label class="form-label small text-muted">Tiles per run</label>
            <input v-model.number="form.max_tiles_per_run" type="number" min="1" class="form-control form-control-sm">
          </div>
          <div class="col-md-4 d-flex align-items-end">
            <div class="form-check form-switch mb-1">
              <input v-model="form.auto_enrich" class="form-check-input" type="checkbox" role="switch" id="pw-go-enrich">
              <label class="form-check-label small" for="pw-go-enrich">Auto-enrich</label>
            </div>
          </div>
        </div>

        <div class="csrc-field mt-2">
          <label class="form-label small text-muted">Categories to keep <small>— all when none picked</small></label>
          <div class="csrc-chips">
            <button
              v-for="c in cnCategoryOptions" :key="c" type="button"
              class="csrc-chip" :class="{ 'csrc-chip--on': form.cn_categories.includes(c) }"
              @click="toggleCnCategory(c)"
            >{{ c }}</button>
          </div>
        </div>

        <div class="csrc-field mt-2">
          <label class="form-label small text-muted">Per-category radius (m) <small>— overrides the default; pubs tight, marinas wider</small></label>
          <div class="csrc-radii">
            <div v-for="c in radiusCatKeys" :key="c" class="csrc-radius">
              <span class="csrc-radius__label">{{ c }}</span>
              <input v-model.number="form.radius_by_category[c]" type="number" min="10" step="10" class="form-control form-control-sm">
            </div>
          </div>
        </div>

        <div class="mt-2">
          <label class="form-label small text-muted">Overpass endpoint <small>— advanced, optional (mirror)</small></label>
          <input v-model="form.overpass_endpoint" class="form-control form-control-sm" placeholder="https://overpass-api.de/api/interpreter">
        </div>
      </template>

      <!-- uk_osm_sweep (Task #49 — national OSM tiled sweep) -->
      <template v-else-if="form.type === 'uk_osm_sweep'">
        <p class="form-text small mb-2">
          <CygnusIcon name="pin" class="me-1" />Sweeps the WHOLE UK (mainland + NI) as a resumable OpenStreetMap tile grid —
          one query per land tile, a bounded budget per run, cursor resumes across runs. Free OSM/Overpass; runs at speed on an
          hourly schedule. Restarts as a refresh cycle when complete.
        </p>
        <div class="row g-2">
          <div class="col-md-4">
            <label class="form-label small text-muted">Tile size (°)</label>
            <input v-model.number="form.tile_deg" type="number" min="0.02" step="0.01" class="form-control form-control-sm">
          </div>
          <div class="col-md-4">
            <label class="form-label small text-muted">Tiles per run</label>
            <input v-model.number="form.max_tiles_per_run" type="number" min="1" class="form-control form-control-sm">
          </div>
          <div class="col-md-4 d-flex align-items-end">
            <div class="form-check form-switch mb-1">
              <input v-model="form.auto_enrich" class="form-check-input" type="checkbox" role="switch" id="pw-uk-enrich">
              <label class="form-check-label small" for="pw-uk-enrich">Auto-enrich</label>
            </div>
          </div>
        </div>

        <!-- Vertical picker — narrows the national sweep to one or more trades -->
        <div class="csrc-field mt-3">
          <label class="form-label small text-muted">
            What kind of business? <small>- pick one or more, or leave empty to sweep every business type</small>
          </label>
          <div v-if="btLoading" class="text-muted small py-1">Loading business types…</div>
          <template v-else>
            <input v-model="btSearch" class="form-control form-control-sm mb-2" placeholder="Search business types - e.g. agency, cafe, plumber">
            <div class="csrc-chips csrc-btlist">
              <button
                v-for="p in filteredBusinessTypes" :key="p.slug" type="button"
                class="csrc-chip" :class="{ 'csrc-chip--on': form.business_types.includes(p.slug) }"
                :title="p.coverage === 'places' ? 'Rarely mapped on OpenStreetMap' : ''"
                @click="toggleBusinessType(p.slug)"
              >{{ p.label }}<span v-if="p.coverage === 'places'" class="csrc-chip__flag">Places</span></button>
            </div>
            <p v-if="coverageHint" class="csrc-hint small mt-2 mb-0">
              <CygnusIcon name="info-circle" class="me-1" />{{ coverageHint }}
            </p>
          </template>
        </div>

        <details class="csrc-adv mt-2">
          <summary>Advanced targeting - broad OSM keys &amp; raw tags</summary>
          <div class="csrc-adv__body">
            <label class="form-label small text-muted">
              Broad categories <small>- all when none picked; used only while no business type is picked above</small>
            </label>
            <div class="csrc-chips">
              <button
                v-for="c in categoryOptions" :key="c" type="button"
                class="csrc-chip" :class="{ 'csrc-chip--on': form.categories.includes(c), 'csrc-chip--muted': form.business_types.length }"
                @click="toggleCategory(c)"
              >{{ c }}</button>
            </div>
            <label class="form-label small text-muted mt-2">
              Raw OSM selectors <small>- one per line, e.g. <code>shop=bakery</code> or a bare key</small>
            </label>
            <textarea v-model="form.categories_raw" rows="3" class="form-control form-control-sm font-monospace" placeholder="craft=plumber&#10;shop=bakery"></textarea>
          </div>
        </details>

        <div class="row g-2 mt-1">
          <div class="col-md-4 d-flex align-items-end">
            <div class="form-check form-switch mb-1">
              <input v-model="form.include_no_website" class="form-check-input" type="checkbox" role="switch" id="pw-uk-nowebsite">
              <label class="form-check-label small" for="pw-uk-nowebsite">Include no-website firms</label>
            </div>
          </div>
          <div class="col-md-4 d-flex align-items-end">
            <div class="form-check form-switch mb-1">
              <input v-model="form.refresh" class="form-check-input" type="checkbox" role="switch" id="pw-uk-refresh">
              <label class="form-check-label small" for="pw-uk-refresh">Refresh when complete</label>
            </div>
          </div>
        </div>

        <div class="mt-2">
          <label class="form-label small text-muted">Overpass URL <small>— advanced, optional (mirror / self-hosted)</small></label>
          <input v-model="form.overpass_url" class="form-control form-control-sm" placeholder="https://overpass-api.de/api/interpreter">
        </div>
        <p class="form-text small text-muted mb-0">
          Fair-use capped at 4000 tiles/day — an over-large tiles-per-run is clamped automatically for the chosen schedule.
        </p>
      </template>

      <!-- ch_sic (phase 2 — Companies House by SIC code + area) -->
      <template v-else-if="form.type === 'ch_sic'">
        <p class="form-text small mb-2">
          <CygnusIcon name="building" class="me-1" />Searches the Companies House register for firms in a
          business type near a place - reaching registered companies with no shopfront, no map pin and no
          Places listing. Free, but rate limited: each run reads a bounded number of pages and resumes where
          it left off. Leads arrive with their registered name, number and office address already filled in.
        </p>

        <div class="row g-2 align-items-end">
          <div class="col-md-8">
            <label class="form-label small text-muted">
              Location <span class="text-danger">*</span> <small>- matched against the registered office address</small>
            </label>
            <input v-model="form.location" class="form-control form-control-sm" placeholder="Brighton">
          </div>
          <div class="col-md-4">
            <label class="form-label small text-muted">Company status</label>
            <select v-model="form.company_status" class="form-select form-select-sm">
              <option value="active">Active only</option>
              <option value="active,liquidation,administration">Active + in difficulty</option>
              <option value="dissolved">Dissolved</option>
            </select>
          </div>
        </div>

        <!-- Vertical picker — a preset's SIC codes are what the register is searched by -->
        <div class="csrc-field mt-3">
          <label class="form-label small text-muted">
            What kind of business? <span class="text-danger">*</span> <small>- pick one or more; their SIC codes are the search</small>
          </label>
          <div v-if="btLoading" class="text-muted small py-1">Loading business types…</div>
          <template v-else>
            <input v-model="btSearch" class="form-control form-control-sm mb-2" placeholder="Search business types - e.g. agency, software, recruitment">
            <div class="csrc-chips csrc-btlist">
              <button
                v-for="p in filteredSicBusinessTypes" :key="p.slug" type="button"
                class="csrc-chip" :class="{ 'csrc-chip--on': form.business_types.includes(p.slug) }"
                :title="'SIC ' + p.sic_codes.join(', ')"
                @click="toggleBusinessType(p.slug)"
              >{{ p.label }}<span class="csrc-chip__flag">{{ p.sic_codes.join(' · ') }}</span></button>
            </div>
            <p v-if="!filteredSicBusinessTypes.length" class="form-text small text-muted mb-0">
              No preset matches “{{ btSearch }}”. Enter the SIC codes directly under Advanced below.
            </p>
            <p v-if="selectedSicCodes.length" class="csrc-hint small mt-2 mb-0">
              <CygnusIcon name="info-circle" class="me-1" />Searching SIC {{ selectedSicCodes.join(', ') }}.
            </p>
          </template>
        </div>

        <details class="csrc-adv mt-2">
          <summary>Advanced targeting - raw SIC codes</summary>
          <div class="csrc-adv__body">
            <label class="form-label small text-muted">
              SIC codes <small>- one per line or comma separated, e.g. <code>62012</code>; added to the presets above</small>
            </label>
            <textarea v-model="form.sic_codes_raw" rows="3" class="form-control form-control-sm font-monospace" placeholder="62012&#10;62020"></textarea>
            <p class="form-text small text-muted mb-0">Anything that isn’t a 4 or 5 digit code is dropped.</p>
          </div>
        </details>

        <div class="row g-2 mt-1">
          <div class="col-md-3">
            <label class="form-label small text-muted">Max results <small>(cap 500)</small></label>
            <input v-model.number="form.max" type="number" min="1" max="500" class="form-control form-control-sm" @change="clampChSicMax">
          </div>
          <div class="col-md-3">
            <label class="form-label small text-muted">Requests per run <small>(cap 30)</small></label>
            <input v-model.number="form.max_requests" type="number" min="1" max="30" class="form-control form-control-sm" @change="clampChSicRequests">
          </div>
          <div class="col-md-4 d-flex align-items-end">
            <div class="form-check form-switch mb-1">
              <input v-model="form.auto_enrich" class="form-check-input" type="checkbox" role="switch" id="pw-ch-enrich">
              <label class="form-check-label small" for="pw-ch-enrich">Auto-enrich</label>
            </div>
          </div>
        </div>
        <p class="form-text small text-muted mb-0">
          Needs a Companies House API key in Settings ▸ Enrichment - the same one enrichment uses, so keep the
          per-run request cap modest. Once the sweep has read the whole result set it parks; reset the source's
          state to run it again.
        </p>
      </template>

      <!-- seed -->
      <template v-else-if="form.type === 'seed'">
        <p class="form-text small text-muted mb-0">No configuration — emits fixed test candidates.</p>
      </template>
    </div>

    <!-- advanced: rate limit + raw JSON (editable — power-user keys round-trip via extraConfig) -->
    <details class="csrc-adv mt-3">
      <summary>Advanced{{ form.type === 'seed' ? ' (JSON)' : '' }}</summary>
      <div class="csrc-adv__body">
        <div v-if="form.type !== 'seed'" class="mb-2" style="max-width:220px">
          <label class="form-label small text-muted">Max fetches/min</label>
          <input v-model.number="form.rate_per_min" type="number" min="1" class="form-control form-control-sm">
        </div>
        <label class="form-label small text-muted">Config (JSON) <small>— kept in sync with the form above; edit to pass through power-user keys</small></label>
        <textarea
          class="form-control form-control-sm font-monospace csrc-adv__json"
          rows="8"
          v-model="advancedText"
          @input="advancedDirty = true"
          @blur="onAdvancedBlur"
        ></textarea>
        <div v-if="advancedError" class="alert alert-warning py-1 px-2 small mt-1 mb-0">{{ advancedError }}</div>
      </div>
    </details>

    <!-- estimated reach sidecar -->
    <div v-if="form.type === 'local_search' || form.type === 'google_places'" class="coverage-box mt-2 mb-1">
      <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="coverageBusy" @click="checkCoverage">
        <CygnusIcon name="shield" class="me-1" />{{ coverageBusy ? 'Checking…' : 'Check reach' }}
      </button>
      <span class="form-text small ms-2">
        {{ form.type === 'google_places'
          ? 'Free OSM count vs a one-page Places probe — is a billed Places call worth it for this area?'
          : 'Estimated coverage from OpenStreetMap for this area.' }}
      </span>
      <div v-if="coverageError" class="alert alert-warning py-1 px-2 small mt-2 mb-0">{{ coverageError }}</div>
      <div v-if="coverage" class="coverage-result mt-2" :class="coverage.recommend_places ? 'coverage-result--yes' : 'coverage-result--no'">
        <div class="coverage-verdict">
          <CygnusIcon :name="coverage.recommend_places ? 'check-circle' : 'minus'" />
          {{ coverage.recommend_places ? 'Places likely worth it' : 'Places may add little' }}
        </div>
        <div class="coverage-stats small">
          <span><strong>OSM</strong> {{ coverage.osm.with_website }} with-site</span>
          <span><strong>Places</strong> {{ coverage.places.available ? (coverage.places.first_page + (coverage.places.has_more ? '+' : '')) : 'no key' }} for “{{ coverage.query }}”</span>
          <span class="text-muted">{{ coverage.radius_miles }}mi · {{ coverage.location || `${coverage.lat}, ${coverage.lon}` }}</span>
        </div>
        <div class="coverage-reason small text-muted">{{ coverage.reason }}</div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * CrawlSourceFields — the typed crawl-source builder (type tiles + per-type
 * config + advanced JSON + reach estimate), lifted out of AdminCrawlSources.vue
 * so the guided PipelineWizard reuses the SAME form UI + config-shaping. It owns
 * only the in-progress form; it never calls the create endpoint. On every change
 * it emits `change` with { type, name, config, valid } so the host decides when
 * to POST. Config shaping is delegated to the shared crawlSourceForm module so a
 * source built here is byte-identical to one built on the Crawl-sources page.
 */
import {
  SOURCE_TYPES, CATEGORY_OPTIONS, CN_CATEGORY_OPTIONS, SCHEDULE_OPTIONS,
  numOr, defaultFormForType, buildTypedConfig, applyConfigToForm, splitExtra, crawlFormValid,
  crawlTypeLock, setBusinessTypeCatalogue, businessTypeCatalogue, businessTypeBySlug, coverageHintFor,
  sicCodesForSlugs,
} from "./util/crawlSourceForm";

export default {
  name: 'CrawlSourceFields',
  props: {
    /** A useApi() instance from the host — shared so auth/token handling is identical. */
    api:         { type: Object, required: true },
    /** Initial source type to open on. */
    initialType: { type: String, default: 'local_search' },
    /** Optional: seed the form from an EXISTING source's config (edit mode). */
    initialConfig: { type: Object, default: null },
    /** Optional: seed the source name (edit mode). */
    initialName: { type: String, default: '' },
  },
  emits: ['change'],
  data() {
    const seededForm = this.initialConfig
      ? Object.assign(applyConfigToForm(this.initialType, this.initialConfig), { name: this.initialName || '' })
      : defaultFormForType(this.initialType);
    return {
      sourceTypes: SOURCE_TYPES,
      categoryOptions: CATEGORY_OPTIONS,
      cnCategoryOptions: CN_CATEGORY_OPTIONS,
      scheduleOptions: SCHEDULE_OPTIONS,
      form: seededForm,
      extraConfig: this.initialConfig ? splitExtra(this.initialType, this.initialConfig) : {},
      advancedText: '{}',
      advancedDirty: false,
      advancedError: null,
      coverage: null,
      coverageBusy: false,
      coverageError: null,
      // Slice 5 §3 — per-plan crawl-type gate map from /me/usage; disabled types
      // render as LOCKED tiles (honest upsell). Empty until loaded → nothing locked.
      crawlTypes: {},
      // Vertical targeting slice 1 — the business-type preset catalogue, served by
      // GET /api/v1/admin/crawl/business-types (no more hardcoded OSM keys here).
      businessTypes: businessTypeCatalogue(),
      btLoading: false,
      btSearch: '',
      // Snapshot of the form as first seeded — lets the catalogue arriving late
      // re-seed an edit-mode form WITHOUT clobbering anything the user changed.
      seedSnapshot: JSON.stringify(seededForm),
    };
  },
  computed: {
    configFromForm() {
      const typed = buildTypedConfig(this.form);
      return { ...this.extraConfig, ...typed };
    },
    valid() {
      return crawlFormValid(this.form);
    },
    radiusCatKeys() {
      return Object.keys(this.form.radius_by_category || {});
    },
    /** Presets matching the search box; picked ones always stay visible. */
    filteredBusinessTypes() {
      const q = this.btSearch.trim().toLowerCase();
      if (!q) return this.businessTypes;
      return this.businessTypes.filter(
        (p) => this.form.business_types.includes(p.slug)
          || p.label.toLowerCase().includes(q)
          || p.slug.includes(q)
          || String(p.places_query || '').toLowerCase().includes(q),
      );
    },
    /**
     * ch_sic can only search presets that carry SIC codes — the register is
     * queried BY code, so a preset without one is not offerable here.
     */
    filteredSicBusinessTypes() {
      return this.filteredBusinessTypes.filter((p) => Array.isArray(p.sic_codes) && p.sic_codes.length > 0);
    },
    /** The SIC codes the picked presets compile to (what the sweep actually searches). */
    selectedSicCodes() {
      return sicCodesForSlugs(this.form.business_types);
    },
    /** Honest "this vertical is barely on OSM" warning for the picked presets. */
    coverageHint() {
      if (this.form.type !== 'local_search' && this.form.type !== 'uk_osm_sweep') return null;
      return coverageHintFor(this.form.business_types);
    },
  },
  watch: {
    configFromForm: {
      immediate: true,
      deep: true,
      handler(val) {
        if (!this.advancedDirty) this.advancedText = JSON.stringify(val, null, 2);
        this.emitChange(val);
      },
    },
    'form.name'() { this.emitChange(); },
    'form.type'() { this.emitChange(); },
  },
  mounted() {
    this.loadCrawlTypes();
    this.loadBusinessTypes();
    this.emitChange();
  },
  methods: {
    lockFor(type) {
      return crawlTypeLock(this.crawlTypes, type);
    },
    /** Fetch the business-type catalogue once per page (module-level cache). */
    async loadBusinessTypes() {
      if (businessTypeCatalogue().length) { this.businessTypes = businessTypeCatalogue(); return; }
      this.btLoading = true;
      try {
        const res = await this.api.get('/api/v1/admin/crawl/business-types');
        const p = res?.data ?? res;
        const list = Array.isArray(p?.data) ? p.data : (Array.isArray(p) ? p : []);
        setBusinessTypeCatalogue(list);
        this.businessTypes = list;
        // Edit mode: the form was seeded before the catalogue existed, so the raw
        // box holds selectors the presets now cover. Re-seed — but ONLY if the
        // user hasn't touched anything since (snapshot compare).
        if (this.initialConfig && JSON.stringify(this.form) === this.seedSnapshot) {
          const preservedName = this.form.name;
          this.form = Object.assign(applyConfigToForm(this.form.type, this.initialConfig), { name: preservedName });
          this.seedSnapshot = JSON.stringify(this.form);
        }
      } catch (e) {
        this.businessTypes = []; // fail-open — broad keys + raw entry still work
      }
      this.btLoading = false;
    },
    toggleBusinessType(slug) {
      const i = this.form.business_types.indexOf(slug);
      if (i === -1) this.form.business_types.push(slug);
      else this.form.business_types.splice(i, 1);
    },
    businessTypeLabel(slug) {
      return (businessTypeBySlug(slug) || {}).label || slug;
    },
    async loadCrawlTypes() {
      try {
        const res = await this.api.get('/api/v1/me/usage');
        const p = res?.data ?? res;
        this.crawlTypes = (p && p.crawl_types) || {};
        // If the tile we opened on is locked for this plan, fall back to the
        // first unlocked type so the form never starts on a type they can't use.
        if (this.lockFor(this.form.type).locked) {
          const first = this.sourceTypes.find((t) => !this.lockFor(t.value).locked);
          if (first) this.selectType(first.value);
        }
      } catch (e) { /* fail-open — no locks shown if usage can't be read */ }
    },
    emitChange(cfg) {
      this.$emit('change', {
        type: this.form.type,
        name: (this.form.name || '').trim(),
        config: cfg || this.configFromForm,
        valid: this.valid,
      });
    },
    selectType(type) {
      if (this.lockFor(type).locked) return; // non-selectable while plan-locked
      const preservedName = this.form.name;
      this.form = defaultFormForType(type);
      this.form.name = preservedName;
      this.extraConfig = {};
      this.advancedDirty = false;
      this.btSearch = '';
      this.coverage = null; this.coverageError = null;
    },
    toggleCategory(c) {
      const i = this.form.categories.indexOf(c);
      if (i === -1) this.form.categories.push(c);
      else this.form.categories.splice(i, 1);
    },
    toggleCnCategory(c) {
      const i = this.form.cn_categories.indexOf(c);
      if (i === -1) this.form.cn_categories.push(c);
      else this.form.cn_categories.splice(i, 1);
    },
    clampGooglePlacesMax() {
      const n = numOr(this.form.max, 40);
      this.form.max = Math.min(Math.max(n, 1), 60);
    },
    clampChSicMax() {
      this.form.max = Math.min(Math.max(numOr(this.form.max, 200), 1), 500);
    },
    clampChSicRequests() {
      this.form.max_requests = Math.min(Math.max(numOr(this.form.max_requests, 20), 1), 30);
    },
    onAdvancedBlur() {
      this.advancedError = null;
      let parsed;
      try {
        parsed = JSON.parse(this.advancedText || '{}');
      } catch (e) {
        this.advancedError = 'Invalid JSON — fix it, or these edits are ignored on save.';
        return;
      }
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        this.advancedError = 'Config must be a JSON object.';
        return;
      }
      const type = this.form.type;
      const preservedName = this.form.name;
      this.form = applyConfigToForm(type, parsed);
      this.form.name = preservedName;
      this.extraConfig = splitExtra(type, parsed);
      this.advancedDirty = false;
    },
    async checkCoverage() {
      this.coverageError = null; this.coverage = null;
      const config = this.configFromForm;
      const hasLoc = (config.location && String(config.location).trim() !== '')
        || (config.lat != null && config.lon != null);
      if (!hasLoc) { this.coverageError = 'Add a location (or lat/lon) first.'; return; }
      this.coverageBusy = true;
      try {
        const params = new URLSearchParams();
        if (config.location) params.set('location', config.location);
        if (config.lat != null) params.set('lat', config.lat);
        if (config.lon != null) params.set('lon', config.lon);
        if (config.query) params.set('query', config.query);
        if (config.radius_miles != null) params.set('radius_miles', config.radius_miles);
        if (Array.isArray(config.categories) && config.categories.length) params.set('categories', config.categories.join(','));
        const res = await this.api.get(`/api/v1/admin/crawl/coverage?${params.toString()}`);
        const p = res?.data ?? res;
        if (p && p.error) this.coverageError = p.error;
        else this.coverage = p;
      } catch (e) {
        this.coverageError = e?.response?.data?.error || e?.message || 'Coverage check failed.';
      }
      this.coverageBusy = false;
    },
  },
};
</script>

<style scoped>
/* Typed crawl-source builder — copied from AdminCrawlSources' non-scoped block so
   this component is visually self-contained wherever it's embedded. */
.csrc-tiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; }
.csrc-tile {
  display: flex; flex-direction: column; align-items: flex-start; gap: 4px; text-align: left;
  border: 1.5px solid var(--m-border); border-radius: var(--m-radius);
  padding: 12px 13px; background: var(--m-bg-card); cursor: pointer; transition: all .12s;
}
.csrc-tile:hover { border-color: var(--m-border-strong); }
.csrc-tile--on {
  border-color: var(--m-accent); background: var(--m-accent-soft);
  box-shadow: 0 0 0 3px var(--m-accent-soft);
}
.csrc-tile__icon { font-size: 1.05rem; color: var(--m-accent); }
.csrc-tile__name { font-size: .83rem; font-weight: 700; color: var(--m-text-primary); }
.csrc-tile__desc { font-size: .7rem; color: var(--m-text-tertiary); line-height: 1.3; }

/* Locked tile — plan-gated crawl type (honest upsell, non-selectable). */
.csrc-tile--locked {
  cursor: not-allowed; opacity: .72; background: var(--m-bg-content);
  border-style: dashed; border-color: var(--m-border);
}
.csrc-tile--locked:hover { border-color: var(--m-border); }
.csrc-tile--locked .csrc-tile__icon { color: var(--m-text-tertiary); }
.csrc-tile--locked .csrc-tile__name { color: var(--m-text-secondary); }
.csrc-tile__lock {
  display: inline-flex; align-items: center; font-size: .68rem; font-weight: 700;
  color: var(--m-accent); background: var(--m-accent-soft);
  border-radius: 999px; padding: 1px 8px; line-height: 1.5;
}

.csrc-field { margin-top: 4px; }
.csrc-slider { display: flex; align-items: center; gap: 12px; }
.csrc-slider .form-range { flex: 1; }
.csrc-slider__val {
  font-size: .8rem; font-weight: 700; font-family: var(--m-font-mono);
  color: var(--m-accent); background: var(--m-accent-soft);
  border-radius: 6px; padding: 2px 9px; flex: none;
}

.csrc-chips { display: flex; flex-wrap: wrap; gap: 7px; }
.csrc-chip {
  font-size: .78rem; font-weight: 600; border-radius: 999px; padding: 4px 12px; cursor: pointer;
  border: 1px solid var(--m-border); color: var(--m-text-secondary); background: var(--m-bg-card);
}
.csrc-chip:hover { border-color: var(--m-border-strong); }
.csrc-chip--on { background: var(--m-accent); border-color: var(--m-accent); color: var(--m-accent-text); }
/* Broad keys are ignored while a business type is picked — say so visually. */
.csrc-chip--muted { opacity: .45; }

/* Business-type (vertical) picker — a long list, so it scrolls in place. */
.csrc-btlist { max-height: 190px; overflow-y: auto; padding: 2px; align-content: flex-start; }
.csrc-chip__flag {
  display: inline-block; margin-left: 6px; font-size: .62rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: .03em; border-radius: 999px;
  padding: 0 6px; background: var(--m-accent-soft); color: var(--m-accent);
}
.csrc-chip--on .csrc-chip__flag { background: var(--m-bg-card); }
.csrc-hint {
  border-left: 3px solid var(--m-status-warning); background: var(--m-bg-content);
  border-radius: 4px; padding: 6px 10px; color: var(--m-text-secondary); line-height: 1.4;
}

/* CN-1 geo_osm — per-category radius editor grid */
.csrc-radii { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 8px; }
.csrc-radius { display: flex; align-items: center; gap: 8px; }
.csrc-radius__label { font-size: .74rem; font-weight: 600; color: var(--m-text-secondary); min-width: 72px; text-transform: capitalize; }
.csrc-radius input { max-width: 80px; }

.csrc-adv { border: 1px solid var(--m-border); border-radius: var(--m-radius); background: var(--m-bg-content); }
.csrc-adv summary { cursor: pointer; font-size: .78rem; font-weight: 600; color: var(--m-text-secondary); padding: 9px 13px; list-style: none; }
.csrc-adv summary::-webkit-details-marker { display: none; }
.csrc-adv__body { padding: 4px 13px 13px; }
.csrc-adv__json { font-size: .76rem; }

.coverage-box { border-top: 1px dashed var(--m-border); padding-top: 10px; }
.coverage-result { border: 1px solid var(--m-border); border-left-width: 4px; border-radius: 6px; padding: 8px 12px; max-width: 560px; }
.coverage-result--yes { border-left-color: var(--m-status-success); background: var(--m-accent-soft); }
.coverage-result--no  { border-left-color: var(--m-status-warning); background: #fffbeb; }
.coverage-verdict { font-weight: 700; font-size: 0.86rem; margin-bottom: 4px; }
.coverage-result--yes .coverage-verdict { color: var(--m-status-success); }
.coverage-result--no  .coverage-verdict { color: var(--m-status-warning); }
.coverage-stats { display: flex; flex-wrap: wrap; gap: 14px; margin-bottom: 4px; }
.coverage-reason { line-height: 1.4; }
</style>
